import { gql } from '@apollo/client';

export const BOOKINGS = gql`
  query Bookings($data: BookingsRequestParams!) {
    bookings(data: $data) {
      results {
        id
        created
        code
        journeyCount
        partnerId
        prepaid
        partner {
          name
          invoicingCurrencyCode
        }
      }
      pagination {
        currentPage
        pageSize
        totalPages
        totalResults
      }
    }
  }
`;

export const BOOKING = gql`
  query Booking($id: Int!) {
    booking(id: $id) {
      id
      created
      updated
      bookerInfo {
        firstName
        lastName
        email
        phone
        companyName
        reference
        traveller
        receiveJourneyUpdates
        languageIsoCode
      }
      code
      pinCode
      travelIntent
      partnerId
      partnerReference
      prepaid
      partner {
        name
      }
      creationInfo {
        source
      }
    }
  }
`;

export const BOOKING_CLONE_DATA = gql`
  query BookingWithJourneys($id: Int!) {
    booking(id: $id) {
      journeyCount
      partner {
        id
        name
      }
      currencyCode
      journeys {
        travellerInfo {
          passengerCount
          luggageCount
        }
        journeyExecutionInfo {
          pickupDate
        }
        originLocation {
          hub
          hubDetails {
            id
            autoCompleteName
          }
          address {
            formattedAddress
          }
        }
        destinationLocation {
          hub
          hubDetails {
            id
            autoCompleteName
          }
          address {
            formattedAddress
          }
        }
      }
    }
  }
`;

export const CREATE_BOOKING = gql`
  mutation CreateBooking($params: BookingParams!) {
    createBooking(params: $params) {
      id
    }
  }
`;

export const JOURNEYS = gql`
  query Journeys($params: JourneysRequestParams!) {
    journeys(params: $params) {
      results {
        id
        created
        booking {
          code
          partner {
            name
            businessModel
          }
        }
        journeyCode
        partnerPriceIncludingVat
        originLocation {
          address {
            formattedAddress
            originalAddress
          }
        }
        destinationLocation {
          address {
            formattedAddress
            originalAddress
          }
        }
        journeyExecutionInfo {
          fleetId
          vehicleCategory
          pickupDate
          pickupTimeZone
          directlyAssigned
        }
        status
        transferCompanies {
          results {
            id
            organization {
              name
            }
          }
        }
        travelAddons {
          type
        }
        priceCalculationInfo {
          currencyCode
        }
        priceSummary {
          priceIncludingVat
          partnerPriceIncludingVat
        }
      }
      pagination {
        currentPage
        pageSize
        totalPages
        totalResults
      }
    }
  }
`;

export const PENDING_JOURNEYS = gql`
  query PendingJourneys($params: PendingJourneyRequestParams!) {
    pendingJourneys(params: $params) {
      results {
        journeyId
        hoursSinceCreation
        hoursUntilPickup
        priority
        journey {
          id
          journeyCode
          created
          booking {
            code
            partner {
              name
            }
          }
          originLocation {
            address {
              formattedAddress
              originalAddress
            }
          }
          destinationLocation {
            address {
              formattedAddress
              originalAddress
            }
          }
          journeyExecutionInfo {
            pickupDate
            pickupTimeZone
          }
          status
        }
      }
      pagination {
        currentPage
        pageSize
        totalPages
        totalResults
      }
    }
  }
`;

export const JOURNEY = gql`
  query Journey($id: Int!, $includeCancellationDetails: Boolean, $refreshComplaints: Boolean, $includePriceSummary: Boolean, $includeHistory: Boolean) {
    journey(id: $id, 
    includeCancellationDetails: $includeCancellationDetails, refreshComplaints: $refreshComplaints, includePriceSummary: $includePriceSummary, includeHistory: $includeHistory) {
      id
      hubId
      quoteId
      journeyCode
      bookingId
      totalPrice
      totalFare
      canBeCompleted
      travellerPrice
      fareIncludingVat
      partnerCommissionPrice
      partnerPriceIncludingVat
      hubMeetingPoints {
        id
        title
        description
        geographicLocation {
          lat
          lng
        }
      }
      booking {
        code
        prepaid
        partnerReference
        partner {
          id
          name
          businessModel
        }
        bookerInfo {
          email
        }
      }
      originLocation {
        address {
          formattedAddress
          originalAddress
        }
        position {
          lat
          lng
        }
        hub
        id
        hubDetails {
          name
        }
      }
      heldForComplaintId
      heldForComplaint {
        id
        status
      }
      hubTender {
        hubId
        meetingPoints {
          results {
            title
            id
          }
        }
        hub {
          id
          name
          meetingPoints {
            title
            id
          }
        }
      }
      destinationLocation {
        address {
          formattedAddress
          originalAddress
        }
        position {
          lat
          lng
        }
        hub
        id
        hubDetails {
          name
        }
      }
      billableJourney {
        id
      }
      priceCalculationInfo {
        currencyCode
        price
        fare
        fareCurrencyCode
        fareVatLevel
        discountPercentage
      }
      priceSummary {
        priceIncludingVat
        fareIncludingVat
        priceIncludingVat
        commissionExcludingVat
        fareIncludingVat
        partnerPriceIncludingVat
      }
      travellerInfo {
        firstName
        lastName
        passengerCount
        luggageCount
        flightNumber
        shipName
        trainNumber
        meetAndGreetSignText
        driverComments
        phone
        email
        languageIsoCode
        residenceCountryCode
      }
      journeyExecutionInfo {
        vehicleCategory
        pickupDate
        pickupTimeZone
        cancellationReason
        fleetId
        driverId
        vehicleId
        meetingPointId
        meetingPointType
        transferCompanyId
        distance {
          value
          unit
        }
        executionCode
        directlyAssigned
        pickupTimeAdjustmentMethod
      }
      cancellationDetails {
        cancellable
        freeCancellationUntil
        freeCancellationWindowHours
      }
      status
      inbound
      transferCompanies {
        results {
          id
          organization {
            name
          }
        }
      }
      travelAddons {
        type
        amount
        priceCalculationInfo {
          currencyCode
          price
          fare
          fareCurrencyCode
          fareVatLevel
        }
      }
      fleet {
        id
        name
        customerSupportInfo {
          phone
          email
          emergencyPhone
          emergencyEmail
        }
      }
      vehicle {
        id
        licensePlate
        vehicleModelId
        vehicleModel {
          name
        }
        vehicleMake {
          name
        }
        vehicleSeries {
          name
        }
      }
      driver {
        id
        name
      }
      meetingPoint {
        id
        title
        description
        geographicLocation {
          lat
          lng
        }
      }
      hub {
        id
        name
        details {
          code
          value
          name
        }
        bufferTimeDomesticInbound
        bufferTimeDomesticOutbound
        bufferTimeInternationalInbound
        bufferTimeInternationalOutbound
        timeZoneId
      }
      statusChangeHistory {
        previousStatus
        newStatus
        comment
        lat
        lng
        comment
      }
    }
  }
`;

export const PAY_BOOKING_BY_INVOICE = gql`
  mutation PayBookingByInvoice($id: Int!, $omitJourneyOrchestration: Boolean!) {
    payBookingByInvoice(id: $id, omitJourneyOrchestration: $omitJourneyOrchestration)
  }
`;

export const CANCEL_BOOKING = gql`
  mutation CancelBooking($id: Int!, $reason: String!) {
    cancelBooking(id: $id, reason: $reason)
  }
`;

export const UPDATE_BOOKER_INFO = gql`
  mutation UpdateBookerInfo($params: BookerInfoParams!) {
    updateBookerInfo(params: $params)
  }
`;

export const APPROVE_JOURNEY = gql`
  mutation ApproveJourney($id: Int!) {
    approveJourney(id: $id)
  }
`;

export const CANCEL_JOURNEY = gql`
  mutation CancelJourney($params: JourneyCancellationParams!) {
    cancelJourney(params: $params)
  }
`;

export const COMPLETE_JOURNEY = gql`
  mutation CompleteJourney($id: Int!) {
    markJourneyAsComplete(id: $id)
  }
`;

export const ASSIGN_FLEET_TO_JOURNEY = gql`
  mutation AssignFleetToJourney($id: Int!, $fleetId: Int!, $meetingPoint: AssignFleetMeetingPointParams) {
    assignFleetToJourney(id: $id, fleetId: $fleetId, meetingPoint: $meetingPoint)
  }
`;

export const ASSIGN_DRIVER_TO_JOURNEY = gql`
  mutation AssignedDriverToJourney($id: Int!, $driverId: Int!) {
    assignedDriverToJourney(id: $id, driverId: $driverId)
  }
`;

export const ASSIGN_VEHICLE_TO_JOURNEY = gql`
  mutation AssignVehicleToJourney($id: Int!, $vehicleId: Int!) {
    assignVehicleToJourney(id: $id, vehicleId: $vehicleId)
  }
`;

export const ASSIGN_MEETING_POINT_TO_JOURNEY = gql`
  mutation AssignMeetingPointToJourney($id: Int!, $meetingPointId: Int!, $type: String!) {
    assignMeetingPointToJourney(id: $id, meetingPointId: $meetingPointId, type: $type)
  }
`;

export const RESEND_BOOKING_RELATED_EMAIL = gql`
  mutation ResendBookingRelatedEmail($params: BookingRelatedEmailParams!) {
    resendBookingRelatedEmail(params: $params)
  }
`;

export const RESEND_JOURNEY_RELATED_EMAIL = gql`
  mutation ResendJourneyRelatedEmail($params: JourneyRelatedEmailParams!) {
    resendJourneyRelatedEmail(params: $params)
  }
`;

export const UPDATE_JOURNEY_TRAVEL_ADDONS = gql`
  mutation UpdateJourneyTravelAddons($params: JourneyTravelAddonsParams!) {
    updateJourneyTravelAddons(params: $params)
  }
`;

export const UPDATE_JOURNEY_FARE = gql`
  mutation UpdateJourneyFare($params: JourneyFareParams!) {
    updateJourneyFare(params: $params)
  }
`;

export const UPDATE_JOURNEY = gql`
  mutation UpdateJourney($id: Int!, $params: JourneyParams!) {
    updateJourney(id: $id, params: $params) {
      id
    }
  }
`;

export const JOURNEY_CHANGES = gql`
  query JourneyChanges($params: JourneyChangesRequestParams!) {
    journeyChanges(params: $params) {
      created
      updated
      journeyId
      changedByUserId
      changedAt
      journeyChangeType
      version
      comment
      status
      paymentLink
      paymentLinkExpiresAt
      user {
        name
        id
      }
      driver {
        name
      }
      valueChanges {
        fieldName
        oldValue
        newValue
        fromOriginLocation {
          address {
            originalAddress
          }
        }
        fromDestinationLocation {
          address {
            originalAddress
          }
        }
        toOriginLocation {
          address {
            originalAddress
          }
        }
        toDestinationLocation {
          address {
            originalAddress
          }
        }
      }
    }
  }
`;

export const TOTAL_REFUNDABLE_AMOUNT = gql`
  query TotalRefundableAmount($id: Int!) {
    totalRefundableAmount(id: $id) {
      amount
      currencyCode
    }
  }
`;

export const PROCESS_JOURNEY_CHANGES = gql`
  mutation ProcessJourneyChanges($params: JourneyParams!) {
    processJourneyChanges(params: $params) {
      id
      journeyId
      changedByUserId
      processedAt
      journeyChangeType
      minorChange
      changeAutomationType
      willSendPaymentLink
      user {
        id
        name
      }
      driver {
        name
      }
      valueChanges {
        fieldName
        oldValue
        newValue
        fromOriginLocation {
          address {
            originalAddress
          }
        }
        fromDestinationLocation {
          address {
            originalAddress
          }
        }
        toOriginLocation {
          address {
            originalAddress
          }
        }
        toDestinationLocation {
          address {
            originalAddress
          }
        }
      }
    }
  }
`;

export const CONFIRM_JOURNEY_CHANGES = gql`
  mutation ConfirmJourneyChanges($id: Int!, $overridePayment: Boolean, $ignoreModificationWindow: Boolean) {
    confirmJourneyChanges(id: $id, overridePayment: $overridePayment, ignoreModificationWindow: $ignoreModificationWindow)
  }
`;

export const GENERATE_PAYMENT_LINK = gql`
  mutation GeneratePaymentLink($id: Int!, $locale: String) {
    generatePaymentLink(id: $id, locale: $locale) {
      url
      expiresAt
    }
  }
`;

export const REFUND_BOOKING = gql`
  mutation RefundBooking($id: Int!, $params: RefundBookingParams!) {
    refundBooking(id: $id, params: $params)
  }
`;

export const ASSIGNED_DRIVER = gql`
  query AssignedDriver($id: Int!, $skip: Boolean!) {
    assignedDriver(id: $id) @skip(if: $skip) {
      id
      name
      mobilePhoneNumber
    }
  }
`;

export const ASSIGNED_VEHICLE = gql`
  query AssignedVehicle($id: Int!, $skip: Boolean!) {
    assignedVehicle(id: $id) @skip(if: $skip) {
      id
      vehicleMake
      vehicleModel
      licensePlate
    }
  }
`;

export const SEND_TRAVELLER_INVOICE = gql`
  mutation SendTravellerInvoice($journeyId: Int!, $params: SendInvoiceParams!) {
    sendTravellerInvoice(journeyId: $journeyId, params: $params)
  }
`;

export const AAJOURNEYS = gql`
  query Journeys($params: JourneysRequestParams!) {
    journeys(params: $params) {
      results {
        id
        inbound
        travellerInfo {
          firstName
          driverComments
          flightNumber
          trainNumber
          shipName
        }
        statusChangeHistory {
          newStatus
          created
          previousStatus
        }
        created
        booking {
          code
          partner {
            id
            name
            businessModel
          }
        }
        journeyCode
        partnerPriceIncludingVat
        originLocation {
          address {
            formattedAddress
            originalAddress
          }
        }
        destinationLocation {
          address {
            formattedAddress
            originalAddress
          }
        }
        journeyExecutionInfo {
          fleetId
          vehicleCategory
          pickupDate
          pickupTimeZone
          directlyAssigned
        }
        status
        hub {
          id
          name
          autoCompleteName
          hubType
        }
        transferCompanies {
          results {
            id
            organization {
              name
            }
          }
        }
        travelAddons {
          type
        }
        priceCalculationInfo {
          currencyCode
        }
        priceSummary {
          priceIncludingVat
          partnerPriceIncludingVat
        }
      }
      pagination {
        currentPage
        pageSize
        totalPages
        totalResults
      }
    }
  }
`;

export const AWAITING_APPROVAL_JOURNEYS = gql`
  query AwaitingApprovalJourneys($params: JourneysRequestParams!) {
    awaitingApprovalJourneys(params: $params) {
      results {
        id
        inbound
        travellerInfo {
          driverComments
          flightNumber
          trainNumber
          shipName
          email
          luggageCount
          meetAndGreetSignText
        }
        statusChangeHistory {
          newStatus
          created
          previousStatus
          comment
        }
        created
        booking {
          code
          partner {
            id
            name
            businessModel
          }
        }
        journeyCode
        partnerPriceIncludingVat
        originLocation {
          hub
          id
          address {
            formattedAddress
            originalAddress
          }
          hubDetails {
            hubType
            name
          }
        }
        destinationLocation {
          hub
          id
          address {
            formattedAddress
            originalAddress
          }
          hubDetails {
            hubType
            name
          }
        }
        journeyExecutionInfo {
          fleetId
          vehicleCategory
          pickupDate
          pickupTimeZone
          directlyAssigned
        }
        status
        hub {
          id
          name
          autoCompleteName
          hubType
        }
        transferCompanies {
          results {
            id
            organization {
              name
            }
          }
        }
        travelAddons {
          type
        }
        priceCalculationInfo {
          currencyCode
        }
        priceSummary {
          priceIncludingVat
          partnerPriceIncludingVat
        }
      }
      pagination {
        currentPage
        pageSize
        totalPages
        totalResults
      }
    }
  }
`;

export const JOURNEY_FOR_QUOTE_DETAILS = gql`
  query Journey($id: Int!, $includeCancellationDetails: Boolean, $refreshComplaints: Boolean, $includePriceSummary: Boolean, $includeHistory: Boolean) {
    journey(id: $id, 
    includeCancellationDetails: $includeCancellationDetails, refreshComplaints: $refreshComplaints, includePriceSummary: $includePriceSummary, includeHistory: $includeHistory) {
      id
      quoteId
      journeyCode
      bookingId
      status
    }
  }
`;
